import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Smart Sticky Header (AnimationSettings → header.stickyHeader).
//
// States of the main Header:
//   top      — the historical header (position:absolute, scrolls away);
//   pinned   — "sticky" mode: full header fixed at the top;
//   compact  — "smart" mode, scrolling down: a small fixed bar (menu + the
//              controls chosen in the settings);
//   expanded — "smart" mode, scrolling up (even slightly): full header fixed.
// The page scroller is .elakayPage (not window). Switching between the
// absolute and fixed positions never moves the page content: the header is
// already out of the flow, and --elakay-header-height is frozen while the
// compact bar is shown (useHeaderHeightVar).

export const STICKY_HEADER_DEFAULTS = Object.freeze({
  enabled: false,
  mode: "normal",
  devices: Object.freeze({ mobile: true, tablet: true, desktop: true }),
  compactHeight: 56,
  transitionDuration: 220,
  hideThreshold: 140,
  scrollTolerance: 8,
  revealTolerance: 4,
  compactItems: Object.freeze({ logo: true, menu: true, search: false, navigation: false, language: false, account: false, cart: true }),
});

export const STICKY_COMPACT_ITEMS = ["logo", "menu", "search", "navigation", "language", "account", "cart"];

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, Math.round(number))) : fallback;
};

// Same rules as server_elakay/elakay_helpers/navigationDisplayRules.js.
export const normalizeStickyHeaderConfig = (raw) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const compactSource = source.compactItems && typeof source.compactItems === "object" ? source.compactItems : {};
  const compactItems = {};
  STICKY_COMPACT_ITEMS.forEach((key) => {
    compactItems[key] = Object.prototype.hasOwnProperty.call(compactSource, key)
      ? compactSource[key] === true
      : STICKY_HEADER_DEFAULTS.compactItems[key];
  });
  compactItems.menu = true;
  const d = STICKY_HEADER_DEFAULTS;
  return {
    enabled: source.enabled === true,
    mode: ["normal", "sticky", "smart"].includes(source.mode) ? source.mode : "normal",
    devices: {
      mobile: source.devices?.mobile !== false,
      tablet: source.devices?.tablet !== false,
      desktop: source.devices?.desktop !== false,
    },
    compactHeight: clamp(source.compactHeight, 44, 80, d.compactHeight),
    transitionDuration: clamp(source.transitionDuration, 0, 600, d.transitionDuration),
    hideThreshold: clamp(source.hideThreshold, 0, 800, d.hideThreshold),
    scrollTolerance: clamp(source.scrollTolerance, 1, 60, d.scrollTolerance),
    revealTolerance: clamp(source.revealTolerance, 1, 60, d.revealTolerance),
    compactItems,
  };
};

export const stickyDeviceForWidth = (width) => (width <= 767 ? "mobile" : width <= 1024 ? "tablet" : "desktop");

export const isStickyHeaderActive = (config, device) => (
  config.enabled && config.mode !== "normal" && config.devices[device] !== false
);

// Pure transition function (unit-tested). `tracker` = { lastY, direction, travel }.
// Tolerances make the decision on accumulated travel in one direction, so
// a few pixels of jitter never toggles the header (no oscillation).
export const nextStickyHeaderState = (state, y, tracker, config, { locked = false } = {}) => {
  const scrollY = Math.max(0, Number(y) || 0);
  const dy = scrollY - tracker.lastY;
  tracker.lastY = scrollY;
  if (dy !== 0) {
    const direction = dy > 0 ? 1 : -1;
    if (direction !== tracker.direction) {
      tracker.direction = direction;
      tracker.travel = 0;
    }
    tracker.travel += Math.abs(dy);
  }
  if (scrollY <= 1) return "top";
  if (config.mode === "sticky") return "pinned";
  if (locked) return state === "top" ? "top" : "expanded";
  if (tracker.direction > 0 && tracker.travel >= config.scrollTolerance && scrollY > config.hideThreshold) return "compact";
  if (tracker.direction < 0 && tracker.travel >= config.revealTolerance && state !== "top") return "expanded";
  return state;
};

const findScroller = (element) => element?.closest?.(".elakayPage") || null;

export const useSmartStickyHeader = (headerRef, rawConfig, { locked = false } = {}) => {
  const config = normalizeStickyHeaderConfig(rawConfig);
  const configKey = JSON.stringify(config);
  const [device, setDevice] = useState(() => (typeof window === "undefined" ? "desktop" : stickyDeviceForWidth(window.innerWidth)));
  const [state, setState] = useState("top");
  const [entering, setEntering] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [focusWithin, setFocusWithin] = useState(false);
  const stateRef = useRef("top");
  const lockedRef = useRef(false);
  const active = isStickyHeaderActive(config, device);
  lockedRef.current = locked || focusWithin;

  useEffect(() => {
    const onResize = () => setDevice(stickyDeviceForWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Keyboard users: focus inside the header always shows the full header.
  useEffect(() => {
    const header = headerRef.current;
    if (!header || !active) return undefined;
    // Keyboard focus (and text fields) only: a mouse click on the compact
    // bar's menu button must not expand the header under the pointer.
    const onFocusIn = (event) => {
      let keyboard = true;
      try { keyboard = event.target.matches(":focus-visible"); } catch (_) { /* older engines */ }
      if (keyboard) setFocusWithin(true);
    };
    const onFocusOut = (event) => {
      if (!header.contains(event.relatedTarget)) setFocusWithin(false);
    };
    header.addEventListener("focusin", onFocusIn);
    header.addEventListener("focusout", onFocusOut);
    return () => {
      header.removeEventListener("focusin", onFocusIn);
      header.removeEventListener("focusout", onFocusOut);
    };
  }, [headerRef, active]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!active || !header) {
      stateRef.current = "top";
      setState("top");
      return undefined;
    }
    const parsed = JSON.parse(configKey);
    const scroller = findScroller(header);
    const target = scroller || window;
    const readY = () => (scroller ? scroller.scrollTop : window.scrollY || 0);
    const tracker = { lastY: readY(), direction: 0, travel: 0 };
    let frame = 0;
    let enterFrame = 0;
    const apply = () => {
      frame = 0;
      const previous = stateRef.current;
      const next = nextStickyHeaderState(previous, readY(), tracker, parsed, { locked: lockedRef.current });
      if (next === previous) return;
      stateRef.current = next;
      if (previous === "top" && next !== "top") {
        // The header arrives from above the viewport instead of popping in.
        setEntering(true);
        cancelAnimationFrame(enterFrame);
        enterFrame = requestAnimationFrame(() => {
          enterFrame = requestAnimationFrame(() => setEntering(false));
        });
      }
      if (scroller) setScrollbarWidth(Math.max(0, scroller.offsetWidth - scroller.clientWidth));
      setState(next);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    target.addEventListener("scroll", onScroll, { passive: true });
    apply();
    return () => {
      target.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(enterFrame);
    };
  }, [headerRef, active, configKey]);

  // A focus/search lock re-evaluates immediately (no scroll needed).
  useEffect(() => {
    if (!active) return;
    if ((locked || focusWithin) && stateRef.current === "compact") {
      stateRef.current = "expanded";
      setState("expanded");
    }
  }, [locked, focusWithin, active]);

  if (!active) return { active: false, state: "top", className: "", style: undefined };

  const hidden = STICKY_COMPACT_ITEMS.filter((key) => !config.compactItems[key]);
  const className = [
    "has-sticky-header",
    state !== "top" ? "is-sticky" : "",
    state === "compact" ? "is-sticky-compact" : "",
    state === "expanded" || state === "pinned" ? "is-sticky-full" : "",
    entering ? "is-sticky-entering" : "",
    ...(state === "compact" ? hidden.map((key) => `sticky-hide-${key}`) : []),
  ].filter(Boolean).join(" ");
  return {
    active: true,
    state,
    className,
    style: {
      "--hdr-sticky-compact-height": `${config.compactHeight}px`,
      "--hdr-sticky-duration": `${config.transitionDuration}ms`,
      "--hdr-sticky-scrollbar": `${scrollbarWidth}px`,
    },
  };
};
